import { useTranslation } from 'react-i18next'
import Select from '../Select'
import { DateRangePickerProps } from './DateRangePicker.types'
import { useDateRangePicker } from './DateRangePicker.utils'
import calendarIcon from '@iconify-icons/akar-icons/calendar'
import { MenuItem, ListItemText } from '@material-ui/core'
import moment from 'moment'
import {
  Container,
  InputWrapper,
  Label,
  Main,
  Icon,
  DatePicker,
  Button,
  Header,
} from './DateRangePicker.style'
import { DEFAULT_MAX_DATE, DEFAULT_MIN_DATE, PICKER_DATE_FORMAT } from 'config'
import { CommonDatePickerProps } from './DateRangePicker.types'

const DateRangePicker = (props: DateRangePickerProps) => {
  const {
    label,
    placeholder,
    fromDatePlaceholder,
    toDatePlaceholder,
    fromLabel,
    toLabel,
    options,
    onClick,
    onToDateChange,
    onFromDateChange,
    id,
  } = props
  const {
    selectValue,
    handleFromDateChange,
    handleToDateChange,
    handleOptionSelect,
    minDate,
    maxDate,
    maxDateRef,
    onFromKeyDown,
    onToKeyDown,
    isOpen,
    onToBlur,
    onFromBlur,
    error,
    localFromDate,
    localToDate,
    handleClose,
    handleClear,
  } = useDateRangePicker(props)
  const { t } = useTranslation()

  const defaultMinDate = moment(DEFAULT_MIN_DATE, PICKER_DATE_FORMAT)
  const defaultMaxDate = moment(DEFAULT_MAX_DATE, PICKER_DATE_FORMAT)

  const commonProps: CommonDatePickerProps = {
    disableToolbar: true,
    variant: 'inline',
    format: PICKER_DATE_FORMAT,
    inputVariant: 'standard',
    keyboardIcon: <Icon icon={calendarIcon} />,
    invalidDateMessage: t('errors:date.wrong'),
  }

  return (
    <Select
      label={label}
      value={selectValue}
      placeholder={placeholder}
      renderNames={false}
      onInputClick={onClick}
      onClose={handleClose}
      isOpen={isOpen}
      error={error}
      id={id}
    >
      <Container>
        <Header>
          <Button id={`${id}-clear-btn`} color="primary" onClick={handleClear}>
            {t('clear')}
          </Button>
        </Header>
        <Main>
          <InputWrapper>
            <Label color="primary">{fromLabel || t('from')}</Label>
            <DatePicker
              {...commonProps}
              placeholder={fromDatePlaceholder || t('datePlaceholder')}
              value={localFromDate}
              minDate={defaultMinDate}
              maxDate={maxDate || defaultMaxDate}
              clearable
              onChange={handleFromDateChange}
              autoOk
              onAccept={date =>
                date && onFromDateChange && onFromDateChange(date)
              }
              maxDateMessage={
                maxDate
                  ? t('errors:filters.maxDateBeforeMinDate')
                  : t('errors:filters.dateShouldBeBefore', {
                      date: DEFAULT_MAX_DATE,
                    })
              }
              minDateMessage={t('errors:filters.dateShouldBeAfter', {
                date: DEFAULT_MIN_DATE,
              })}
              inputProps={{
                ref: maxDateRef,
              }}
              InputProps={{
                onKeyDown: onFromKeyDown,
                onBlur: onFromBlur,
              }}
              id={`${id}-min`}
            />
          </InputWrapper>
          <InputWrapper>
            <Label color="primary">{toLabel || t('to')}</Label>
            <DatePicker
              {...commonProps}
              placeholder={toDatePlaceholder || t('datePlaceholder')}
              value={localToDate}
              minDate={minDate || defaultMinDate}
              maxDate={defaultMaxDate}
              clearable
              autoOk
              onChange={handleToDateChange}
              inputProps={{
                ref: maxDateRef,
              }}
              InputProps={{
                onKeyDown: onToKeyDown,
                onBlur: onToBlur,
              }}
              onAccept={date => date && onToDateChange && onToDateChange(date)}
              maxDateMessage={t('errors:filters.dateShouldBeBefore', {
                date: DEFAULT_MAX_DATE,
              })}
              minDateMessage={
                minDate
                  ? t('errors:filters.minDateAfterMaxDate')
                  : t('errors:filters.dateShouldBeAfter', {
                      date: DEFAULT_MIN_DATE,
                    })
              }
              id={`${id}-max`}
            />
          </InputWrapper>
        </Main>
        {options?.map(option => (
          <MenuItem
            key={`Select-option-${option.title}`}
            value={option.value}
            onClick={() => handleOptionSelect(option.value)}
          >
            <ListItemText primary={option.title} />
          </MenuItem>
        ))}
      </Container>
    </Select>
  )
}

export default DateRangePicker
